import { Logger } from "../../clients/logger";
import ConfigService from "./configService";
import { RouterClient } from "../../clients/routerClient";
import { httpGet, httpPost, httpPut, httpDelete } from "../../common/REST";
import InteropAdmin from "../../clients/Interop/InteropAdmin";
import { System } from "../../common/system";
import { AppDefinition } from "../Interop/types";
import { appdToComponent } from "./configService";

const interopAdmin = new InteropAdmin();

const SMART_DESKTOP_TOPIC = "SmartDesktopClient";
let HTTP_BASE: string | null | undefined = null;

async function removeAppInConfigServiceAndPublish(key: string) {
	Logger.system.log("SmartDesktopClientHandler.removeAppInConfigServiceAndPublish", key);

	delete ConfigService.manifest.finsemble.components[key];
	delete ConfigService.manifest.finsemble.appd[key];

	const finsembleConfigObject = {
		appd: ConfigService.manifest.finsemble.appd,
		components: ConfigService.manifest.finsemble.components,
	};

	const PFCParams = {
		manifest: ConfigService.manifest,
		allowOverwrites: true,
		finsembleConfigObject,
		replaceApps: false,
		replaceComponents: false,
		replaceServices: false,
		updateStore: true,
	};

	await ConfigService.processFinsembleConfig(PFCParams, () => {});
	await interopAdmin.addAppDefinitions(ConfigService.manifest.finsemble.appd);

	// @deprecated remove in 7.0
	RouterClient.publish("Config.changeNotification", {
		removedApp: key,
		finsemble: ConfigService.manifest.finsemble,
	});
}

async function updateAppInConfigServiceAndPublish(appConfig: AppDefinition, updateExistingDefinitions = false) {
	Logger.system.log("SmartDesktopClientHandler.updateAppInConfigServiceAndPublish", appConfig);

	const finsembleConfigObject = {
		appd: {
			[appConfig.appId]: appConfig,
		},
		components: {
			[appConfig.appId]: appdToComponent(appConfig),
		},
	};

	const PFCParams = {
		manifest: ConfigService.manifest,
		allowOverwrites: updateExistingDefinitions,
		finsembleConfigObject,
		replaceApps: !updateExistingDefinitions,
		replaceComponents: !updateExistingDefinitions,
		replaceServices: false,
		updateStore: true,
	};

	await ConfigService.processFinsembleConfig(PFCParams, () => {});
	await interopAdmin.addAppDefinitions(ConfigService.manifest.finsemble.appd);

	// @deprecated remove in 7.0
	RouterClient.publish("Config.changeNotification", {
		componentConfig: ConfigService.manifest.finsemble.components[appConfig.appId],
		finsemble: ConfigService.manifest.finsemble,
	});
}

async function getMenuHandler(request) {
	let { err, getData } = await httpGet(`${HTTP_BASE}/toolbar/menus`);
	request.sendQueryResponse(err, getData);
}
async function addMenuHandler(request) {
	let { err } = await httpPost(`${HTTP_BASE}/toolbar/menus/add/`, request.data.menuConfig);
	request.sendQueryResponse(err);
}
async function updateMenuHandler(request) {
	let { err } = await httpPut(`${HTTP_BASE}/toolbar/menus/${request.data.menuId}`, request.data.menuConfig);
	request.sendQueryResponse(err);
}
async function deleteMenuHandler(request) {
	let { err } = await httpDelete(`${HTTP_BASE}/toolbar/menus/${request.data.menuId}`);
	request.sendQueryResponse(err);
}

async function getPreloadsHandler(request) {
	let { err, getData } = await httpGet(`${HTTP_BASE}/project/preloads`);
	request.sendQueryResponse(err, getData);
}
async function savePreloadHandler(request) {
	let { err } = await httpPut(`${HTTP_BASE}/project/preloads`, {
		oldAlias: request.data.oldAlias,
		newAlias: request.data.newAlias,
		detail: request.data.detail,
	});
	request.sendQueryResponse(err);
}
async function deletePreloadHandler(request) {
	let { err } = await httpDelete(`${HTTP_BASE}/project/preloads/${request.data.alias}`);
	request.sendQueryResponse(err);
}

async function getAppsHandler(request) {
	let { err, getData } = await httpGet(`${HTTP_BASE}/appd/v1/apps`);
	request.sendQueryResponse(err, getData);
}

async function getAppConfigHandler(request) {
	Logger.system.log("SmartDesktopClientHandler getAppConfigHandler", request);
	let error;
	let response;
	const appId = request.data?.appId;

	if (appId) {
		let { err, getData: appConfig } = await httpGet(`${HTTP_BASE}/appd/v1/apps/${appId}`);
		error = err;
		response = appConfig;
	} else {
		error = "smartDesktopClientHandler:getAppConfigHandler() no appId provided";
	}
	request.sendQueryResponse(error, response);
	if (error) Logger.error(error, request.data);
}

async function addAppHandler(request) {
	Logger.system.log("SmartDesktopClientHandler addAppHandler", request);
	let error;
	const appConfig: AppDefinition = request?.data?.appConfig;

	if (appConfig?.appId) {
		await updateAppInConfigServiceAndPublish(appConfig);
		let { err } = await httpPost(`${HTTP_BASE}/appd/v1/apps`, appConfig);
		error = err;
	} else {
		error = "smartDesktopClientHandler:addAppHandler() received app definition with no appId";
	}
	request.sendQueryResponse(error);
	if (error) Logger.error(error, request.data);
}

async function updateAppHandler(request) {
	Logger.system.log("SmartDesktopClientHandler updateAppHandler", request);
	let error;
	const appConfig: AppDefinition = request?.data?.appConfig;

	if (appConfig?.appId) {
		await updateAppInConfigServiceAndPublish(appConfig, true);
		let { err } = await httpPut(`${HTTP_BASE}/appd/v1/apps/${appConfig.appId}`, appConfig);
		error = err;
	} else {
		error = "smartDesktopClientHandler:updateHandler() received app definition with no appId";
	}
	request.sendQueryResponse(error);
	if (error) Logger.error(error, request.data);
}

async function deleteAppHandler(request) {
	Logger.system.log("SmartDesktopClientHandler deleteAppHandler", request);
	let error;
	const appId = request.data?.appId;
	if (appId) {
		let { err } = await httpDelete(`${HTTP_BASE}/appd/v1/apps/${appId}`);
		if (!err) await removeAppInConfigServiceAndPublish(appId);
		error = err;
	} else {
		error = "smartDesktopClientHandler:deleteAppHandler() no appId provided";
	}
	request.sendQueryResponse(error);
	if (error) Logger.error(error, request.data);
}

async function updateThemeHandler(request) {
	// See smartDesktopClient
	Logger.system.log("SmartDesktopClientHandler updateThemeHandler", request);
	let { err } = await httpPut(`/project/assets/theme`, request.data.theme);
	RouterClient.transmit("Finsemble-ui-change.themeUpdate", request.data.theme);

	// update the config store, which will also trigger any listeners
	// ConfigService.configStore.setValue({
	// 	field: "finsemble.theme",
	// 	value: request.data.theme,
	// });
	request.sendQueryResponse(err);
}

async function getThemeHandler(request) {
	Logger.system.log("SmartDesktopClientHandler getThemeHandler", request);
	let { err, getData } = await httpGet(`/project/assets/theme`);

	request.sendQueryResponse(err, getData);
}

async function refreshSystemTrayIconConfigHandler(request) {
	Logger.system.log("SmartDesktopClientHandler updateSystemTrayIconHandler", request);
	// update the config store, which will also trigger any listeners (e.g. LoadSystemTrayIconTask in system manager is a listener)
	ConfigService.configStore?.setValue({
		field: "finsemble.systemTrayIcon",
		value: ConfigService.manifest.finsemble.systemTrayIcon,
	});
	request.sendQueryResponse(null);
}

// helper function that handles any router error when adding a responder
function addHandler(topic: string, handler) {
	RouterClient.addResponder(topic, (responderError, responderRequest) => {
		if (responderError) {
			Logger.system.error(`"SmartDesktopClientHandler responder error for ${handler.name}: ${responderError}`);
		} else {
			handler(responderRequest);
		}
	});
}

function initializeHandlers() {
	addHandler(`${SMART_DESKTOP_TOPIC}.getApps`, getAppsHandler);
	addHandler(`${SMART_DESKTOP_TOPIC}.getMenus`, getMenuHandler);
	addHandler(`${SMART_DESKTOP_TOPIC}.getAppConfig`, getAppConfigHandler);
	addHandler(`${SMART_DESKTOP_TOPIC}.getPreloads`, getPreloadsHandler);
	addHandler(`${SMART_DESKTOP_TOPIC}.addApp`, addAppHandler);
	addHandler(`${SMART_DESKTOP_TOPIC}.addMenu`, addMenuHandler);
	addHandler(`${SMART_DESKTOP_TOPIC}.updateApp`, updateAppHandler);
	addHandler(`${SMART_DESKTOP_TOPIC}.updateMenu`, updateMenuHandler);
	addHandler(`${SMART_DESKTOP_TOPIC}.savePreload`, savePreloadHandler);
	addHandler(`${SMART_DESKTOP_TOPIC}.deleteApp`, deleteAppHandler);
	addHandler(`${SMART_DESKTOP_TOPIC}.deleteMenu`, deleteMenuHandler);
	addHandler(`${SMART_DESKTOP_TOPIC}.deletePreload`, deletePreloadHandler);
	addHandler(`${SMART_DESKTOP_TOPIC}.updateTheme`, updateThemeHandler);
	addHandler(`${SMART_DESKTOP_TOPIC}.getTheme`, getThemeHandler);
	addHandler(`${SMART_DESKTOP_TOPIC}.refreshSystemTrayIconConfig`, refreshSystemTrayIconConfigHandler);
}

/**
 * Initializes the smart desktop client API by obtaining the location of the smart desktop server.
 */
const initialize = async () =>
	new Promise<void>(async (resolve) => {
		Logger.system.log("SmartDesktopClientHandler.initialize");
		System.getSDServerInfo((info) => {
			HTTP_BASE = info.applicationRoot;
			initializeHandlers();
			resolve();
		});
	});

// HERE move http functions into their own file
export default { initialize };
